if (!customElements.get('event-checkout-form')) {
  class EventCheckoutForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.section = this.closest('.shopify-section, dialog');
      this.quantityInput = this.querySelector('input[name="quantity"]');
      this.variantInput = this.querySelector('input[name="variant_id"]');
      this.countrySelect = this.querySelector('select[name="attributes[nazione]"]');
      this.submitButton = this.querySelector('[data-submit-button]');
      this.submitButtonText = this.querySelector('[data-submit-button-text]');
      this.errorMessage = this.querySelector('[data-error-message]');
      this.participantsContainer = this.querySelector('[data-participants-container]');
      this.addMemberButton = this.querySelector('[data-add-participant="socio"]');
      this.isSubmitting = false;
      this.isCheckoutDateValid = this.dataset.checkoutDateValid !== 'false';
      this.isVariantAvailable = false;
      this.participants = [];
      this.nextParticipantId = 1;

      this.defaultButtonText = this.submitButtonText?.textContent?.trim() || 'Procedi al checkout';
      this.loadingButtonText = this.getAttribute('data-loading-label') || 'Reindirizzamento...';

      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
      this.handleAddMember = this.handleAddMember.bind(this);
      this.handleParticipantInput = this.handleParticipantInput.bind(this);
      this.handleParticipantAction = this.handleParticipantAction.bind(this);

      this.form.addEventListener('submit', this.handleSubmit);
      this.section?.addEventListener('variant:update', this.handleVariantUpdate);
      this.addMemberButton?.addEventListener('click', this.handleAddMember);
      this.participantsContainer?.addEventListener('input', this.handleParticipantInput);
      this.participantsContainer?.addEventListener('click', this.handleParticipantAction);

      this.setDefaultCountry();
      this.setInitialVariantState();
      this.syncQuantityFromParticipants();
      this.renderParticipants();
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.section?.removeEventListener('variant:update', this.handleVariantUpdate);
      this.addMemberButton?.removeEventListener('click', this.handleAddMember);
      this.participantsContainer?.removeEventListener('input', this.handleParticipantInput);
      this.participantsContainer?.removeEventListener('click', this.handleParticipantAction);
    }

    handleVariantUpdate(event) {
      if (!event?.detail) return;

      if (event.detail.data?.newProduct?.id) {
        this.dataset.productId = event.detail.data.newProduct.id;
      } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
        return;
      }

      const variant = event.detail.resource;
      if (!variant) return;

      const inventoryQuantity = Number.parseInt(variant.inventory_quantity, 10);
      if (Number.isFinite(inventoryQuantity)) {
        this.dataset.availability = String(Math.max(0, inventoryQuantity));
      }

      if (this.variantInput) {
        this.variantInput.value = variant.id || '';
      }

      if (this.submitButton) {
        this.isVariantAvailable = Boolean(variant.available);
      }

      if (this.quantityInput) {
        const max = variant.quantity_rule?.max;
        this.quantityInput.min = '1';
        this.quantityInput.step = '1';

        if (max !== null && max !== undefined) {
          this.quantityInput.max = String(max);
        } else {
          this.quantityInput.removeAttribute('max');
        }
      }

      this.enforceParticipantLimits();
      this.syncQuantityFromParticipants();
      this.renderParticipants();
      this.updateSubmitButtonState();
    }

    getInitialVariantFromPicker() {
      const productId = this.dataset.productId;
      const scopedSelector = productId
        ? `variant-picker[data-product-id="${productId}"] script[type="application/json"]`
        : 'variant-picker script[type="application/json"]';
      const variantScript =
        this.section?.querySelector(scopedSelector) ||
        this.section?.querySelector('variant-picker script[type="application/json"]');

      if (!variantScript?.textContent) return null;

      try {
        return JSON.parse(variantScript.textContent);
      } catch (error) {
        console.warn('Unable to parse initial variant JSON for event checkout.', error);
        return null;
      }
    }

    setInitialVariantState() {
      const initialVariant = this.getInitialVariantFromPicker();

      if (initialVariant?.id && this.variantInput && !this.variantInput.value) {
        this.variantInput.value = String(initialVariant.id);
      }

      if (this.quantityInput) {
        const max = initialVariant?.quantity_rule?.max;
        this.quantityInput.min = '1';
        this.quantityInput.step = '1';

        if (max !== null && max !== undefined) {
          this.quantityInput.max = String(max);
        } else {
          this.quantityInput.removeAttribute('max');
        }
      }

      if (typeof initialVariant?.available === 'boolean') {
        this.isVariantAvailable = initialVariant.available;
        return;
      }

      this.isVariantAvailable = Boolean(this.variantInput?.value);
    }

    setDefaultCountry() {
      if (!this.countrySelect) return;
      const preferred = this.dataset.defaultCountry || 'Italy';
      const fallback = preferred === 'Italy' ? 'Italia' : 'Italy';

      if (this.countrySelect.value && this.countrySelect.value !== '---') return;

      if (this.countrySelect.querySelector(`option[value="${preferred}"]`)) {
        this.countrySelect.value = preferred;
        return;
      }

      if (this.countrySelect.querySelector(`option[value="${fallback}"]`)) {
        this.countrySelect.value = fallback;
      }
    }

    getQuantity() {
      const parsedQuantity = Number.parseInt(this.quantityInput?.value || '1', 10);
      return Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
    }

    getAvailability() {
      const availability = Number.parseInt(this.dataset.availability || '', 10);
      return Number.isFinite(availability) && availability >= 0 ? availability : null;
    }

    getMaxQuantity() {
      const variantMax = Number.parseInt(this.quantityInput?.getAttribute('max') || '', 10);
      const limits = [
        this.getAvailability(),
        Number.isFinite(variantMax) && variantMax > 0 ? variantMax : null,
      ].filter((limit) => limit !== null);

      return limits.length > 0 ? Math.min(...limits) : null;
    }

    canIncreaseQuantity() {
      const maxQuantity = this.getMaxQuantity();
      if (maxQuantity === null) return true;

      return this.participants.length + 2 <= maxQuantity;
    }

    canAddParticipant() {
      return this.canIncreaseQuantity();
    }

    syncQuantityFromParticipants() {
      if (!this.quantityInput) return;

      this.quantityInput.value = String(this.participants.length + 1);
    }

    enforceParticipantLimits() {
      const maxQuantity = this.getMaxQuantity();
      const maxExtras = maxQuantity === null ? null : Math.max(maxQuantity - 1, 0);

      if (maxExtras !== null && this.participants.length > maxExtras) {
        this.participants = this.participants.slice(0, maxExtras);
      }

    }

    renderParticipants() {
      if (!this.participantsContainer) return;

      if (this.participants.length === 0) {
        this.participantsContainer.innerHTML = '';
        this.participantsContainer.classList.add('hidden');
        this.updateActionButtons();
        this.updateSubmitButtonState();
        return;
      }

      this.participantsContainer.innerHTML = '';
      const fragment = document.createDocumentFragment();
      const members = this.participants.filter((participant) => participant.type === 'socio');

      if (members.length > 0) {
        fragment.appendChild(this.createParticipantGroup('Partecipanti aggiuntivi', members));
      }

      this.participantsContainer.appendChild(fragment);
      this.participantsContainer.classList.remove('hidden');
      this.updateActionButtons();
      this.updateSubmitButtonState();
    }

    createParticipantGroup(titleText, participants) {
      const group = document.createElement('div');
      group.className = 'course-checkout-block__participant-group';

      const title = document.createElement('p');
      title.className = 'course-checkout-block__participant-group-title';
      title.textContent = titleText;
      group.appendChild(title);

      participants.forEach((participant, index) => {
        group.appendChild(this.createParticipantFieldset(participant, index + 1));
      });

      return group;
    }

    createParticipantFieldset(participant, participantIndex) {
      const fieldset = document.createElement('fieldset');
      fieldset.className = 'course-checkout-block__participant';

      const header = document.createElement('div');
      header.className = 'course-checkout-block__participant-header';

      const title = document.createElement('p');
      title.className = 'course-checkout-block__participant-title';
      title.textContent = `Partecipante aggiuntivo #${participantIndex}`;
      header.appendChild(title);

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'course-checkout-block__participant-remove button-unstyled';
      removeButton.dataset.removeParticipant = String(participant.id);
      removeButton.textContent = 'Rimuovi';
      header.appendChild(removeButton);

      fieldset.appendChild(header);

      const fields = document.createElement('div');
      fields.className = 'course-checkout-block__participant-fields';

      fields.appendChild(this.createParticipantField(participant, 'firstName', 'Nome', 'given-name'));
      fields.appendChild(this.createParticipantField(participant, 'lastName', 'Cognome', 'family-name'));

      fieldset.appendChild(fields);
      return fieldset;
    }

    createParticipantField(participant, fieldName, labelText, autocomplete, isFullWidth = false) {
      const wrapper = document.createElement('div');
      wrapper.className = 'course-checkout-block__participant-field';
      if (isFullWidth) {
        wrapper.classList.add('course-checkout-block__participant-field--full');
      }

      const safeFieldName = `participant_${participant.id}_${fieldName}`;

      const label = document.createElement('label');
      label.setAttribute('for', `${this.dataset.blockId}-${safeFieldName}`);
      label.textContent = labelText;

      const input = document.createElement('input');
      input.id = `${this.dataset.blockId}-${safeFieldName}`;
      input.name = safeFieldName;
      input.type = 'text';
      input.autocomplete = autocomplete;
      input.dataset.participantId = String(participant.id);
      input.dataset.participantField = fieldName;
      input.value = participant[fieldName] || '';
      input.required = true;

      wrapper.appendChild(label);
      const inputWrapper = document.createElement('div');
      inputWrapper.className = 'course-checkout-block__participant-input-wrapper';
      inputWrapper.appendChild(input);

      wrapper.appendChild(inputWrapper);

      return wrapper;
    }

    handleAddMember() {
      this.addParticipant('socio');
    }

    addParticipant(type) {
      if (!this.canAddParticipant(type)) return;

      this.participants.push({
        id: this.nextParticipantId,
        type,
        firstName: '',
        lastName: '',
      });
      this.nextParticipantId += 1;

      this.syncQuantityFromParticipants();
      this.renderParticipants();
    }

    handleParticipantInput(event) {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;

      const participantId = Number.parseInt(input.dataset.participantId || '', 10);
      const participantField = input.dataset.participantField;
      if (!Number.isFinite(participantId) || !participantField) return;

      const participant = this.participants.find((entry) => entry.id === participantId);
      if (!participant) return;

      participant[participantField] = input.value;
    }

    handleParticipantAction(event) {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const removeButton = target.closest('[data-remove-participant]');
      if (!(removeButton instanceof HTMLElement)) return;

      const participantId = Number.parseInt(removeButton.dataset.removeParticipant || '', 10);
      if (!Number.isFinite(participantId)) return;

      this.participants = this.participants.filter((participant) => participant.id !== participantId);
      this.syncQuantityFromParticipants();
      this.renderParticipants();
      this.updateSubmitButtonState();
    }

    updateActionButtons() {
      if (this.addMemberButton) {
        this.addMemberButton.disabled = !this.canAddParticipant();
      }
    }

    updateSubmitButtonState() {
      if (!this.submitButton) return;

      const hasVariantId = Boolean(this.variantInput?.value);
      const maxQuantity = this.getMaxQuantity();
      const shouldDisable =
        this.isSubmitting ||
        !this.isCheckoutDateValid ||
        !hasVariantId ||
        !this.isVariantAvailable ||
        (maxQuantity !== null && this.getQuantity() > maxQuantity);

      this.submitButton.disabled = shouldDisable;
    }

    showError(message) {
      if (!this.errorMessage) return;
      this.errorMessage.textContent = message;
      this.errorMessage.classList.remove('hidden');
    }

    clearError() {
      if (!this.errorMessage) return;
      this.errorMessage.textContent = '';
      this.errorMessage.classList.add('hidden');
    }

    setSubmittingState(isSubmitting) {
      if (!this.submitButton) return;

      this.isSubmitting = isSubmitting;
      this.updateSubmitButtonState();
      this.submitButton.setAttribute('aria-busy', isSubmitting ? 'true' : 'false');

      if (this.submitButtonText) {
        this.submitButtonText.textContent = isSubmitting ? this.loadingButtonText : this.defaultButtonText;
      }
    }

    buildExtraParticipantAttributeEntries() {
      const attributes = [];
      let participantIndex = 0;

      for (const participant of this.participants) {
        const firstName = String(participant.firstName || '').trim();
        const lastName = String(participant.lastName || '').trim();

        participantIndex += 1;
        if (firstName) {
          attributes.push([`partecipante_aggiuntivo_${participantIndex}_nome`, firstName]);
        }
        if (lastName) {
          attributes.push([`partecipante_aggiuntivo_${participantIndex}_cognome`, lastName]);
        }
      }

      return attributes;
    }

    extractAttributeName(fieldName) {
      if (typeof fieldName !== 'string') return null;
      if (!fieldName.startsWith('attributes[') || !fieldName.endsWith(']')) return null;

      return fieldName.slice(11, -1).trim();
    }

    buildCartAttributeEntries() {
      const formData = new FormData(this.form);
      const attributes = [];

      for (const [fieldName, value] of formData.entries()) {
        const attributeName = this.extractAttributeName(fieldName);
        if (!attributeName) continue;

        const normalizedValue = String(value || '').trim();
        if (normalizedValue !== '') {
          attributes.push([attributeName, normalizedValue]);
        }
      }

      attributes.push(...this.buildExtraParticipantAttributeEntries());

      return attributes;
    }

    buildCartAttributesPayload() {
      const legacyAttributeNames = [
        'Nome',
        'Cognome',
        'Numero tessera',
        'Codice Fiscale',
        'Email',
        'Indirizzo',
        'Città',
        'Provincia',
        'CAP',
        'Nazione',
        'Data di Nascita',
        'Città di Nascita',
        'Provincia di Nascita',
        'Telefono',
        'Partecipanti aggiuntivi',
        'Accompagnatori',
      ];
      const payload = legacyAttributeNames.reduce((attributes, attributeName) => {
        attributes[attributeName] = '';
        return attributes;
      }, {});

      return this.buildCartAttributeEntries().reduce((attributes, [attributeName, attributeValue]) => {
        attributes[attributeName] = attributeValue;
        return attributes;
      }, payload);
    }

    extractPropertyName(fieldName) {
      if (typeof fieldName !== 'string') return null;
      if (!fieldName.startsWith('properties[') || !fieldName.endsWith(']')) return null;

      return fieldName.slice(11, -1).trim();
    }

    buildLineItemPropertiesPayload() {
      const formData = new FormData(this.form);
      const properties = {};

      for (const [fieldName, value] of formData.entries()) {
        const propertyName = this.extractPropertyName(fieldName);
        if (!propertyName) continue;

        const normalizedValue = String(value || '').trim();
        if (normalizedValue !== '') {
          properties[propertyName] = normalizedValue;
        }
      }

      return properties;
    }

    buildCheckoutItems(variantId, quantity) {
      const properties = this.buildLineItemPropertiesPayload();
      const item = {
        id: variantId,
        quantity,
      };

      if (Object.keys(properties).length > 0) {
        item.properties = properties;
      }

      return [
        item,
      ];
    }

    getRootPath() {
      const rootPath = window.Shopify?.routes?.root || '/';
      return rootPath.endsWith('/') ? rootPath : `${rootPath}/`;
    }

    getCartAddUrl() {
      return window.Theme?.routes?.cart_add_url || `${this.getRootPath()}cart/add.js`;
    }

    getCartUpdateUrl() {
      return window.Theme?.routes?.cart_update_url || `${this.getRootPath()}cart/update`;
    }

    getCartClearUrl() {
      return `${this.getRootPath()}cart/clear.js`;
    }

    getCheckoutUrl() {
      const checkoutUrl = `${this.getRootPath()}checkout`;
      const formData = new FormData(this.form);
      const formFields = this.dataset.checkoutPrefillSource === 'customer'
        ? { email: 'customer_email', firstName: 'customer_first_name', lastName: 'customer_last_name' }
        : { email: 'attributes[email]', firstName: 'attributes[nome]', lastName: 'attributes[cognome]' };
      const mappings = [
        ['checkout[email]', formFields.email],
        ['checkout[shipping_address][first_name]', formFields.firstName],
        ['checkout[shipping_address][last_name]', formFields.lastName],
      ];
      const searchParams = new URLSearchParams();

      mappings.forEach(([checkoutField, formField]) => {
        const value = String(formData.get(formField) || '').trim();
        if (value) searchParams.set(checkoutField, value);
      });

      const queryString = searchParams.toString();
      return queryString ? `${checkoutUrl}?${queryString}` : checkoutUrl;
    }

    async postCartJson(url, payload = {}) {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });

      let data = null;

      try {
        data = await response.json();
      } catch (error) {
        data = null;
      }

      if (!response.ok || data?.status) {
        const errorMessage = data?.description || data?.message || `HTTP ${response.status}`;
        throw new Error(errorMessage);
      }

      return data;
    }

    async submitCheckoutCart(variantId, quantity) {
      const items = this.buildCheckoutItems(variantId, quantity);

      await this.postCartJson(this.getCartClearUrl());

      await this.postCartJson(this.getCartAddUrl(), { items });

      const attributes = this.buildCartAttributesPayload();
      if (Object.keys(attributes).length > 0) {
        await this.postCartJson(this.getCartUpdateUrl(), { attributes });
      }

      return this.getCheckoutUrl();
    }

    async handleSubmit(event) {
      event.preventDefault();

      if (!this.isCheckoutDateValid) return;
      if (!this.form.reportValidity()) return;

      const variantId = Number.parseInt(this.variantInput?.value || '', 10);
      const quantity = this.getQuantity();

      if (!Number.isFinite(variantId) || variantId <= 0) {
        this.showError('Variante non valida.');
        return;
      }

      this.clearError();
      this.setSubmittingState(true);

      try {
        const checkoutUrl = await this.submitCheckoutCart(variantId, quantity);
        window.location.assign(checkoutUrl);
      } catch (error) {
        this.showError(error instanceof Error ? error.message : 'Si e verificato un errore inatteso.');
        this.setSubmittingState(false);
      }
    }
  }

  customElements.define('event-checkout-form', EventCheckoutForm);
}
